import React from "react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { Box, Typography, CircularProgress, Alert } from "@mui/material";
import { useWidgetData } from "../../hooks/useWidgets";
import WidgetHeader from "./WidgetHeader";
import { chartColors, rechartsTheme } from "../../utils/chartTheme";

const ChartWidget = ({ config, onEdit, onDelete }) => {
  const { data, isLoading, error } = useWidgetData(
    config.dataSource,
    config.timeRange
  );

  const renderChart = () => {
    if (!data) return null;

    const chartData =
      data.labels?.map((label, index) => ({
        name: label,
        value: data.values?.[index] || 0,
      })) || [];

    const colors = chartColors;

    switch (config.chartType) {
      case "line":
        return (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke={rechartsTheme.gridStroke} />
              <XAxis dataKey="name" stroke={rechartsTheme.axisStroke} tick={{ fill: rechartsTheme.tickFill }} />
              <YAxis stroke={rechartsTheme.axisStroke} tick={{ fill: rechartsTheme.tickFill }} />
              <RechartsTooltip contentStyle={rechartsTheme.tooltipContentStyle} />
              {config.settings?.showLegend && (
                <Legend wrapperStyle={rechartsTheme.legendStyle} />
              )}
              <Line
                type="monotone"
                dataKey="value"
                stroke={config.settings?.color || chartColors[1]}
                strokeWidth={2}
                dot={{ fill: config.settings?.color || chartColors[1] }}
              />
            </LineChart>
          </ResponsiveContainer>
        );

      case "bar":
        return (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke={rechartsTheme.gridStroke} />
              <XAxis dataKey="name" stroke={rechartsTheme.axisStroke} tick={{ fill: rechartsTheme.tickFill }} />
              <YAxis stroke={rechartsTheme.axisStroke} tick={{ fill: rechartsTheme.tickFill }} />
              <RechartsTooltip contentStyle={rechartsTheme.tooltipContentStyle} />
              {config.settings?.showLegend && (
                <Legend wrapperStyle={rechartsTheme.legendStyle} />
              )}
              <Bar dataKey="value" fill={config.settings?.color || chartColors[0]} />
            </BarChart>
          </ResponsiveContainer>
        );

      case "pie":
        return (
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) =>
                  `${name} ${(percent * 100).toFixed(0)}%`
                }
                outerRadius={80}
                fill={chartColors[1]}
                dataKey="value"
              >
                {chartData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={colors[index % colors.length]}
                  />
                ))}
              </Pie>
              <RechartsTooltip contentStyle={rechartsTheme.tooltipContentStyle} />
              {config.settings?.showLegend && (
                <Legend wrapperStyle={rechartsTheme.legendStyle} />
              )}
            </PieChart>
          </ResponsiveContainer>
        );

      case "doughnut":
        return (
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius={40}
                outerRadius={80}
                fill={chartColors[1]}
                dataKey="value"
                label={({ name, percent }) =>
                  `${name} ${(percent * 100).toFixed(0)}%`
                }
              >
                {chartData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={colors[index % colors.length]}
                  />
                ))}
              </Pie>
              <RechartsTooltip contentStyle={rechartsTheme.tooltipContentStyle} />
              {config.settings?.showLegend && (
                <Legend wrapperStyle={rechartsTheme.legendStyle} />
              )}
            </PieChart>
          </ResponsiveContainer>
        );

      default:
        return (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            height={300}
          >
            <Typography color="text.secondary">
              Unsupported chart type: {config.chartType}
            </Typography>
          </Box>
        );
    }
  };

  const renderSummary = () => {
    if (!data) return null;

    const summaryItems = [];

    if (data.total !== undefined) {
      summaryItems.push({ label: "Total", value: data.total });
    }
    if (data.overall !== undefined) {
      summaryItems.push({ label: "Overall", value: data.overall });
    }
    if (data.completed !== undefined) {
      summaryItems.push({ label: "Completed", value: data.completed });
    }
    if (data.active !== undefined) {
      summaryItems.push({ label: "Active", value: data.active });
    }

    if (summaryItems.length === 0) return null;

    return (
      <Box
        display="flex"
        gap={2}
        mt={2}
        p={2}
        bgcolor="background.paper"
        borderRadius={1}
      >
        {summaryItems.map((item, index) => (
          <Box key={index} textAlign="center">
            <Typography variant="h6" color="primary">
              {item.value?.toLocaleString()}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {item.label}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Box
      sx={{
        p: 2,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.paper",
        borderRadius: 4,
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      <WidgetHeader
        title={config.title}
        onEdit={onEdit ? () => onEdit(config) : null}
        onDelete={onDelete ? () => onDelete(config) : null}
        showDragHandle={Boolean(onEdit || onDelete)}
      />

      {/* Widget Content */}
      <Box flex={1} display="flex" flexDirection="column" minHeight={0}>
        {isLoading ? (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            flex={1}
          >
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : (
          <>
            {renderChart()}
            {renderSummary()}
          </>
        )}
      </Box>
    </Box>
  );
};

export default ChartWidget;
